import { audio, rand, mtof, type Voice } from './engine.js';

/**
 * Sound effects, each built from a few oscillators and filtered noise bursts.
 * `v` carries distance volume and stereo pan from the caller.
 */
const A = audio;
const R = (v: Voice, gain: number, reverb = 0.12): Voice => ({ ...v, gain: (v.gain ?? 1) * gain, reverb });

export type Surface = 'grass' | 'path' | 'sand' | 'wood' | 'rock' | 'mud' | 'stone';

export const sfx = {
  /* ---- forestry ---- */
  chop(v: Voice = {}) {
    const p = rand(0.9, 1.12);
    A.noiseHit({ ...R(v, 0.55), filter: 'bandpass', freq: 1500 * p, q: 1.2, dur: 0.11 });
    A.tone({ ...R(v, 0.5), type: 'sine', freq: 170 * p, to: 70, dur: 0.16 });
    A.tone({ ...R(v, 0.18), type: 'triangle', freq: 420 * p, to: 260, dur: 0.06 });
  },
  logGot(v: Voice = {}) {
    A.noiseHit({ ...R(v, 0.45), filter: 'lowpass', freq: 900, dur: 0.18 });
    A.tone({ ...R(v, 0.2, 0.2), type: 'triangle', freq: mtof(79), dur: 0.25, at: 0.05 });
  },
  treeFall(v: Voice = {}) {
    A.tone({ ...R(v, 0.18, 0.25), type: 'sawtooth', freq: 95, to: 55, dur: 0.9, attack: 0.25, filter: { type: 'bandpass', freq: 700, q: 4 } });
    A.noiseHit({ ...R(v, 0.35, 0.3), filter: 'lowpass', freq: 1800, to: 300, dur: 0.6, at: 0.75, attack: 0.04 });
    A.tone({ ...R(v, 0.55, 0.3), type: 'sine', freq: 90, to: 40, dur: 0.5, at: 0.8 });
  },
  /* ---- mining ---- */
  mine(v: Voice = {}) {
    const f = rand(820, 1050);
    A.bell(f, [[1, 0.28], [2.76, 0.16], [5.4, 0.08]], 0.32, R(v, 1, 0.15));
    A.noiseHit({ ...R(v, 0.35), filter: 'highpass', freq: 2500, dur: 0.05 });
    A.tone({ ...R(v, 0.35), freq: 130, to: 70, dur: 0.12 });
  },
  oreGot(v: Voice = {}) { A.bell(rand(1300, 1500), [[1, 0.2], [2.4, 0.1]], 0.5, R(v, 1, 0.25)); },
  rockBreak(v: Voice = {}) {
    for (let i = 0; i < 4; i++) A.noiseHit({ ...R(v, 0.3, 0.2), filter: 'lowpass', freq: rand(500, 1200), dur: rand(0.12, 0.25), at: i * rand(0.04, 0.09) });
    A.tone({ ...R(v, 0.4), freq: 80, to: 45, dur: 0.35 });
  },
  /* ---- fishing ---- */
  cast(v: Voice = {}) {
    A.noiseHit({ ...R(v, 0.25), filter: 'bandpass', freq: 1200, to: 3800, q: 2, dur: 0.28, attack: 0.08 });
    A.tone({ ...R(v, 0.25, 0.2), freq: rand(700, 900), to: 260, dur: 0.14, at: 0.32 });
    A.noiseHit({ ...R(v, 0.18, 0.2), filter: 'highpass', freq: 2200, dur: 0.18, at: 0.32 });
  },
  catchFish(v: Voice = {}) {
    A.noiseHit({ ...R(v, 0.5, 0.25), filter: 'highpass', freq: 1400, to: 600, dur: 0.45 });
    A.tone({ ...R(v, 0.35), freq: 320, to: 110, dur: 0.22 });
    A.tone({ ...R(v, 0.18, 0.3), type: 'triangle', freq: mtof(84), dur: 0.3, at: 0.18 });
  },
  /** A net sweeps through the water. */
  net(v: Voice = {}) {
    A.noiseHit({ ...R(v, 0.3), filter: 'bandpass', freq: 700, to: 2200, q: 1.2, dur: 0.35, attack: 0.1 });
    A.noiseHit({ ...R(v, 0.3, 0.2), filter: 'highpass', freq: 1600, to: 700, dur: 0.4, at: 0.3 });
    for (let i = 0; i < 4; i++) A.noiseHit({ ...R(v, 0.1), filter: 'highpass', freq: rand(2500, 5000), dur: 0.03, at: 0.55 + i * rand(0.05, 0.1) });
  },
  /** A harpoon thrown and hitting water. */
  harpoon(v: Voice = {}) {
    A.noiseHit({ ...R(v, 0.25), filter: 'bandpass', freq: 900, to: 4200, q: 3, dur: 0.22, attack: 0.03 });
    A.tone({ ...R(v, 0.35, 0.2), freq: 260, to: 90, dur: 0.18, at: 0.22 });
    A.noiseHit({ ...R(v, 0.4, 0.25), filter: 'lowpass', freq: 1500, to: 400, dur: 0.35, at: 0.22 });
  },
  /** A trap or pot dropped in. */
  trapSet(v: Voice = {}) {
    A.tone({ ...R(v, 0.45, 0.25), freq: 180, to: 70, dur: 0.25 });
    A.noiseHit({ ...R(v, 0.35, 0.25), filter: 'lowpass', freq: 1200, to: 300, dur: 0.5 });
    for (let i = 0; i < 5; i++) A.tone({ ...R(v, 0.06, 0.3), freq: rand(500, 900), to: rand(1200, 1800), dur: 0.05, at: 0.25 + i * rand(0.08, 0.16) });
  },
  /** Rope and dripping water as a trap comes up. */
  trapHaul(v: Voice = {}) {
    for (let i = 0; i < 3; i++) A.noiseHit({ ...R(v, 0.2), filter: 'bandpass', freq: rand(600, 900), q: 4, dur: 0.12, at: i * 0.18 });
    A.noiseHit({ ...R(v, 0.4, 0.25), filter: 'highpass', freq: 1200, to: 500, dur: 0.7, at: 0.5 });
    for (let i = 0; i < 6; i++) A.tone({ ...R(v, 0.05, 0.3), freq: rand(1500, 3000), to: rand(800, 1200), dur: 0.03, at: 0.7 + i * rand(0.06, 0.15) });
  },
  /** Something takes the bait: a plop and a tug. */
  bite(v: Voice = {}) {
    A.tone({ ...R(v, 0.4, 0.2), freq: 900, to: 220, dur: 0.12 });
    A.tone({ ...R(v, 0.35, 0.2), freq: 700, to: 180, dur: 0.12, at: 0.16 });
    A.tone({ ...R(v, 0.18), type: 'triangle', freq: mtof(81), dur: 0.12, at: 0.05 });
  },
  /** A good strike: the reel screams. */
  strike(v: Voice = {}) {
    for (let i = 0; i < 9; i++) A.tone({ ...R(v, 0.09), type: 'square', freq: 1400 + i * 30, dur: 0.018, at: i * 0.028, filter: { type: 'bandpass', freq: 2200, q: 4 } });
    A.bell(mtof(84), [[1, 0.16], [2, 0.06]], 0.5, { ...R(v, 1, 0.3), at: 0.25 });
  },
  /** A personal best or a world record. */
  record(world = false) {
    const notes = world ? [72, 76, 79, 84, 88] : [76, 79, 84];
    notes.forEach((n, i) => A.bell(mtof(n), [[1, 0.17], [2, 0.06], [3, 0.03]], 0.9, { bus: 'sfx', reverb: 0.4, at: i * 0.11 }));
  },
  /** A legend comes out of the water. */
  legend() {
    [60, 67, 72, 76, 79, 84].forEach((n, i) => A.bell(mtof(n), [[1, 0.18], [2.01, 0.07], [3, 0.04]], 1.8, { bus: 'sfx', reverb: 0.6, at: i * 0.14 }));
    A.noiseHit({ bus: 'sfx', gain: 0.25, filter: 'lowpass', freq: 1200, to: 200, dur: 1.2, reverb: 0.5 });
  },
  /* ---- combat ---- */
  swing(v: Voice = {}, heavy = false) { A.noiseHit({ ...R(v, heavy ? 0.4 : 0.3), filter: 'bandpass', freq: heavy ? 600 : 1100, to: heavy ? 1600 : 2600, q: 1.5, dur: heavy ? 0.22 : 0.15, attack: 0.04 }); },
  hit(v: Voice = {}, big = false) {
    A.noiseHit({ ...R(v, big ? 0.6 : 0.45, 0.12), filter: 'lowpass', freq: big ? 900 : 1400, to: 200, dur: 0.14 });
    A.tone({ ...R(v, big ? 0.5 : 0.35), freq: big ? 120 : 170, to: 55, dur: 0.16 });
  },
  /** A body or a heavy thing hitting the ground. */
  thud(v: Voice = {}) {
    A.noiseHit({ ...R(v, 0.45, 0.1), filter: 'lowpass', freq: 380, to: 90, dur: 0.28 });
    A.tone({ ...R(v, 0.35), freq: 85, to: 40, dur: 0.3 });
  },
  /** Thunder rolling in from far off: low, long and soft; louder as the storm nears. */
  thunder(pan = 0, near = 0.5) {
    const g = 0.12 + near * 0.25;
    A.noiseHit({ gain: g, pan, filter: 'lowpass', freq: 260, to: 80, dur: 2.6, attack: 0.25, reverb: 0.6 });
    A.noiseHit({ gain: g * 0.6, pan, filter: 'lowpass', freq: 500, to: 120, dur: 1.2, attack: 0.05, at: 0.35, reverb: 0.5 });
  },
  /** A weak point glinting in the rock. */
  glint() { A.bell(rand(2400, 2700), [[1, 0.14], [2.4, 0.06]], 0.5, { gain: 0.5 }); A.bell(rand(3200, 3500), [[1, 0.08]], 0.4, { gain: 0.3, at: 0.07 }); },
  /** Something rare turned up while mining. */
  find() { [76, 83, 88].forEach((m, i) => A.bell(mtof(m), [[1, 0.12], [2, 0.05]], 0.9, { gain: 0.45, at: i * 0.09 })); },
  /** A masterwork off the anvil. */
  masterwork() { [60, 67, 72, 76, 79].forEach((m, i) => A.tone({ gain: 0.12, type: 'triangle', freq: mtof(m), dur: 1.1, at: i * 0.11, reverb: 0.5 })); A.bell(mtof(91), [[1, 0.1]], 1.6, { gain: 0.4, at: 0.55 }); },
  /** The bar's heat as you strike it: brighter metal rings higher. */
  forgeStrike(score: number) { A.bell(1100 + score * 380, [[1, 0.14], [2.76, 0.07], [5.4, 0.03]], 0.35 + score * 0.1, { gain: 0.6 }); A.noiseHit({ gain: 0.25, filter: 'highpass', freq: 3000, dur: 0.05 }); },
  /** A small flock bursting up out of the grass. */
  flutter(v: Voice = {}) { for (let i = 0; i < 6; i++) A.noiseHit({ ...R(v, 0.12, 0.25), filter: 'bandpass', freq: rand(600, 1300), q: 1.2, dur: 0.05, at: i * 0.045 + Math.random() * 0.02 }); },
  /** Soft rustle of picking a plant. */
  rustle(v: Voice = {}) { for (let i = 0; i < 2; i++) A.noiseHit({ ...R(v, 0.16, 0.2), filter: 'bandpass', freq: rand(2500, 4200), q: 1.5, dur: 0.09, at: i * 0.08 }); },
  /** Knife on wood. */
  whittle(v: Voice = {}) { A.noiseHit({ ...R(v, 0.14, 0.2), filter: 'highpass', freq: 2600, to: 1800, dur: 0.12 }); },
  clang(v: Voice = {}) { A.bell(rand(1500, 1800), [[1, 0.12], [2.7, 0.06]], 0.25, R(v, 1, 0.15)); },
  miss(v: Voice = {}) { A.noiseHit({ ...R(v, 0.18), filter: 'highpass', freq: 1800, to: 4000, dur: 0.12 }); },
  bowRelease(v: Voice = {}) {
    A.tone({ ...R(v, 0.3), type: 'triangle', freq: 180, to: 90, dur: 0.08 });
    A.noiseHit({ ...R(v, 0.2), filter: 'bandpass', freq: 2500, to: 1200, q: 2, dur: 0.2, at: 0.02 });
  },
  arrowHit(v: Voice = {}) { A.noiseHit({ ...R(v, 0.35), filter: 'bandpass', freq: 900, q: 2, dur: 0.06 }); A.tone({ ...R(v, 0.2), freq: 300, to: 120, dur: 0.08 }); },
  frost(v: Voice = {}) {
    A.noiseHit({ ...R(v, 0.3, 0.4), filter: 'highpass', freq: 3000, to: 6000, dur: 0.6, attack: 0.1 });
    for (let i = 0; i < 5; i++) A.bell(rand(2400, 3600), [[1, 0.05]], 0.4, { ...R(v, 1, 0.5), at: i * 0.06 });
  },
  hurt(v: Voice = {}) { A.tone({ ...R(v, 0.35), type: 'triangle', freq: 220, to: 140, dur: 0.18 }); A.noiseHit({ ...R(v, 0.3), filter: 'lowpass', freq: 800, dur: 0.12 }); },
  /** A special is winding up near you: a rising warning. */
  warn(v: Voice = {}) {
    A.tone({ ...R(v, 0.25, 0.2), type: 'sawtooth', freq: 140, to: 320, dur: 0.6, attack: 0.1, filter: { type: 'lowpass', freq: 900, q: 2 } });
    A.tone({ ...R(v, 0.15, 0.2), type: 'square', freq: 660, dur: 0.06, at: 0.45, filter: { type: 'bandpass', freq: 1200, q: 3 } });
  },
  special(v: Voice = {}) { A.tone({ ...R(v, 0.3, 0.3), type: 'sawtooth', freq: 200, to: 900, dur: 0.25, filter: { type: 'lowpass', freq: 2000, q: 3 } }); A.bell(mtof(79), [[1, 0.15], [2, 0.06]], 0.6, { ...R(v, 1, 0.4), at: 0.15 }); },
  creature(kind: string, v: Voice = {}) {
    const g = (x: number) => R(v, x, 0.2);
    switch (kind) {
      case 'cluck': for (let i = 0; i < 3; i++) A.tone({ ...g(0.12), type: 'square', freq: rand(600, 760), to: 420, dur: 0.06, at: i * 0.1, filter: { type: 'bandpass', freq: 1200, q: 4 } }); break;
      case 'moo': A.tone({ ...g(0.22), type: 'sawtooth', freq: 140, to: 110, dur: 0.9, attack: 0.1, filter: { type: 'lowpass', freq: 600, q: 3 } }); break;
      case 'baa': A.tone({ ...g(0.18), type: 'sawtooth', freq: 320, to: 290, dur: 0.5, attack: 0.04, filter: { type: 'bandpass', freq: 900, q: 3 } }); for (let i = 0; i < 6; i++) A.tone({ ...g(0.04), freq: 320, dur: 0.04, at: 0.05 + i * 0.07 }); break;
      case 'squeak': A.tone({ ...g(0.12), freq: rand(1800, 2400), to: 2900, dur: 0.08 }); break;
      case 'grunt': for (let i = 0; i < 2; i++) A.noiseHit({ ...g(0.3), color: 'brown', filter: 'lowpass', freq: 300, dur: 0.14, at: i * 0.18 }); break;
      case 'growl': A.tone({ ...g(0.22), type: 'sawtooth', freq: 90, to: 75, dur: 0.8, attack: 0.1, filter: { type: 'lowpass', freq: 500, q: 6 } }); A.noiseHit({ ...g(0.12), color: 'brown', filter: 'bandpass', freq: 300, q: 3, dur: 0.8 }); break;
      case 'croak': for (let i = 0; i < 2; i++) A.tone({ ...g(0.18), type: 'square', freq: 95, to: 80, dur: 0.22, at: i * 0.3, filter: { type: 'bandpass', freq: 450, q: 5 } }); break;
      case 'gurgle': for (let i = 0; i < 6; i++) A.tone({ ...g(0.12), freq: rand(80, 200), to: rand(200, 400), dur: 0.08, at: i * 0.09 }); break;
      case 'screech': A.tone({ ...g(0.12), type: 'sawtooth', freq: 3200, to: 4200, dur: 0.18, filter: { type: 'bandpass', freq: 3500, q: 4 } }); break;
      case 'cackle': for (let i = 0; i < 4; i++) A.tone({ ...g(0.12), type: 'square', freq: rand(400, 520), to: 300, dur: 0.07, at: i * 0.09, filter: { type: 'bandpass', freq: 900, q: 3 } }); break;
      case 'wail': A.tone({ ...g(0.15, ), freq: 520, to: 780, dur: 1.2, attack: 0.3, reverb: 0.6 }); A.tone({ ...g(0.08), freq: 524, to: 790, dur: 1.2, attack: 0.3, reverb: 0.6 }); break;
      case 'roar': A.tone({ ...g(0.35), type: 'sawtooth', freq: 70, to: 45, dur: 1.4, attack: 0.15, filter: { type: 'lowpass', freq: 450, q: 4 } }); A.noiseHit({ ...g(0.3), color: 'brown', filter: 'lowpass', freq: 500, to: 200, dur: 1.3, attack: 0.1 }); break;
    }
  },
  creatureDie(v: Voice = {}) { A.tone({ ...R(v, 0.25, 0.2), type: 'triangle', freq: 300, to: 80, dur: 0.5 }); A.noiseHit({ ...R(v, 0.25), color: 'brown', filter: 'lowpass', freq: 500, dur: 0.35, at: 0.3 }); },
  /** Loot drops: brighter for rarer finds. */
  drop(rarity: string) {
    if (rarity === 'very_rare') { [72, 79, 84, 88, 91].forEach((n, i) => A.bell(mtof(n), [[1, 0.16], [2, 0.06], [3, 0.03]], 1.2, { bus: 'sfx', reverb: 0.5, at: i * 0.09 })); return; }
    if (rarity === 'rare' || rarity === 'shared') { [76, 83, 88].forEach((n, i) => A.bell(mtof(n), [[1, 0.15], [2, 0.05]], 0.9, { bus: 'sfx', reverb: 0.4, at: i * 0.1 })); return; }
    if (rarity === 'uncommon') { A.bell(mtof(81), [[1, 0.12]], 0.5, { bus: 'sfx', reverb: 0.3 }); return; }
    A.noiseHit({ bus: 'sfx', gain: 0.18, filter: 'lowpass', freq: 1200, dur: 0.12 });
  },
  death() { [60, 57, 53, 48].forEach((n, i) => A.bell(mtof(n), [[1, 0.2], [2.01, 0.06]], 1.6, { bus: 'sfx', reverb: 0.6, at: i * 0.3 })); },
  equip(v: Voice = {}) { A.noiseHit({ ...R(v, 0.25), filter: 'bandpass', freq: 3000, q: 2, dur: 0.06 }); A.bell(rand(1200, 1400), [[1, 0.08]], 0.3, R(v, 1, 0.1)); },
  /* ---- cooking ---- */
  sizzle(v: Voice = {}) {
    A.noiseHit({ ...R(v, 0.3), filter: 'highpass', freq: 3200, dur: 0.9, attack: 0.05 });
    for (let i = 0; i < 6; i++) A.noiseHit({ ...R(v, 0.18), filter: 'bandpass', freq: rand(2500, 6000), q: 3, dur: 0.03, at: rand(0, 0.8) });
  },
  cooked(v: Voice = {}) { A.bell(mtof(81), [[1, 0.16], [3, 0.05]], 0.6, R(v, 1, 0.3)); A.bell(mtof(88), [[1, 0.12]], 0.7, { ...R(v, 1, 0.3), at: 0.09 }); },
  burnt(v: Voice = {}) { A.tone({ ...R(v, 0.25), type: 'triangle', freq: mtof(62), dur: 0.25 }); A.tone({ ...R(v, 0.25), type: 'triangle', freq: mtof(58), dur: 0.4, at: 0.16 }); },
  /* ---- smithing ---- */
  hammer(v: Voice = {}) {
    const f = rand(480, 560);
    A.bell(f, [[1, 0.3], [2.32, 0.2], [4.25, 0.12], [6.1, 0.06]], 1.1, R(v, 1, 0.3));
    A.noiseHit({ ...R(v, 0.4), filter: 'highpass', freq: 3000, dur: 0.04 });
  },
  smelt(v: Voice = {}) {
    A.noiseHit({ ...R(v, 0.45, 0.2), color: 'brown', filter: 'lowpass', freq: 300, to: 900, dur: 0.9, attack: 0.25 });
    for (let i = 0; i < 4; i++) A.tone({ ...R(v, 0.1), freq: rand(180, 320), to: rand(400, 600), dur: 0.08, at: rand(0.2, 0.8) });
  },
  barGot(v: Voice = {}) { A.noiseHit({ ...R(v, 0.3), filter: 'highpass', freq: 2500, to: 5000, dur: 0.6 }); A.bell(rand(700, 760), [[1, 0.15], [2.7, 0.06]], 0.8, R(v, 1, 0.3)); },
  /* ---- obstacles ---- */
  clear(v: Voice = {}, kind = 'rockslide') {
    if (kind === 'bramble') for (let i = 0; i < 3; i++) A.noiseHit({ ...R(v, 0.35), filter: 'bandpass', freq: rand(1800, 3000), q: 2, dur: 0.07, at: i * 0.11 });
    else if (kind === 'bridge') { sfx.hammer(R(v, 0.7)); }
    else if (kind === 'stones') A.tone({ ...R(v, 0.2, 0.3), freq: 600, to: 300, dur: 0.15 });
    else sfx.rockBreak(v);
  },
  /* ---- movement ---- */
  step(surface: Surface, v: Voice = {}) {
    const g = (v.gain ?? 1) * 0.16;
    switch (surface) {
      case 'wood': A.tone({ ...v, gain: g * 1.6, type: 'triangle', freq: rand(150, 190), to: 110, dur: 0.08 }); A.noiseHit({ ...v, gain: g, filter: 'bandpass', freq: 700, dur: 0.05 }); break;
      case 'path': A.noiseHit({ ...v, gain: g * 1.3, filter: 'bandpass', freq: rand(1500, 2400), q: 1.5, dur: 0.07 }); break;
      case 'sand': A.noiseHit({ ...v, gain: g * 1.1, filter: 'lowpass', freq: rand(700, 1000), dur: 0.1 }); break;
      case 'stone': case 'rock': A.noiseHit({ ...v, gain: g * 1.2, filter: 'highpass', freq: 2200, dur: 0.035 }); A.tone({ ...v, gain: g * 0.8, freq: 260, to: 160, dur: 0.04 }); break;
      case 'mud': A.noiseHit({ ...v, gain: g * 1.2, filter: 'lowpass', freq: 420, dur: 0.14 }); break;
      default: A.noiseHit({ ...v, gain: g, filter: 'lowpass', freq: rand(900, 1300), dur: 0.08 });
    }
  },
  /* ---- rewards and UI ---- */
  coins() { [mtof(91), mtof(96)].forEach((f, i) => A.bell(f, [[1, 0.13], [2.01, 0.05]], 0.5, { at: i * 0.07, reverb: 0.2 })); },
  /** Someone nearby levelled up: the same rising chord, small and placed. */
  levelUpNear(v: Voice = {}) { [60, 64, 67, 72].forEach((m, i) => A.tone({ ...R(v, 0.05), type: 'triangle', freq: mtof(m + 12), dur: 0.7, at: i * 0.08, reverb: 0.4 })); },
  levelUp(big = false) {
    audio.duck(0.3, big ? 4 : 2.8);
    if (big) [84, 88, 91, 96, 100].forEach((m, i) => A.bell(mtof(m), [[1, 0.09], [2.76, 0.03]], 1.6, { at: 0.9 + i * 0.12, reverb: 0.6 }));
    [60, 64, 67, 72, 76].forEach((m, i) => { A.tone({ type: 'triangle', freq: mtof(m + 12), dur: 0.9, gain: 0.14, at: i * 0.09, reverb: 0.35 }); A.tone({ freq: mtof(m + 24), dur: 1.1, gain: 0.06, at: i * 0.09, reverb: 0.4 }); });
    [72, 76, 79].forEach(m => A.tone({ type: 'sine', freq: mtof(m), dur: 2.2, gain: 0.08, at: 0.5, attack: 0.08, reverb: 0.5 }));
    A.noiseHit({ gain: 0.06, filter: 'highpass', freq: 6000, to: 9000, dur: 1.6, attack: 0.4, at: 0.3, reverb: 0.5 });
  },
  /** A quest step done (soft), a quest started, or a quest finished (a short fanfare). */
  quest(state: 'start' | 'step' | 'done') {
    if (state === 'done') { audio.duck(0.3, 2.5); [55, 59, 62, 67, 71].forEach((m, i) => A.tone({ type: 'triangle', freq: mtof(m + 12), dur: 0.8, gain: 0.12, at: i * 0.11, reverb: 0.4 })); A.tone({ type: 'sine', freq: mtof(79), dur: 2, gain: 0.07, at: 0.6, attack: 0.1, reverb: 0.5 }); return; }
    const notes = state === 'start' ? [62, 69] : [67, 74];
    notes.forEach((m, i) => A.tone({ type: 'triangle', freq: mtof(m + 12), dur: 0.5, gain: 0.08, at: i * 0.1, reverb: 0.35 }));
  },
  discover() {
    audio.duck(0.35, 3.5);
    [62, 66, 69, 74].forEach((m, i) => A.tone({ type: 'sine', freq: mtof(m), dur: 3.2, gain: 0.07, attack: 0.6 + i * 0.1, reverb: 0.6 }));
    A.bell(mtof(86), [[1, 0.1], [2.76, 0.04]], 2.2, { at: 0.4, reverb: 0.6 });
    A.bell(mtof(90), [[1, 0.08]], 2.2, { at: 0.75, reverb: 0.6 });
  },
  landmark() {
    audio.duck(0.4, 3);
    A.bell(mtof(74), [[1, 0.16], [2.76, 0.07], [5.4, 0.03]], 3.4, { reverb: 0.7 });
    A.bell(mtof(81), [[1, 0.1], [2.76, 0.04]], 3, { at: 0.35, reverb: 0.7 });
  },
  pickup() { A.tone({ type: 'triangle', freq: mtof(84), dur: 0.12, gain: 0.08 }); A.tone({ freq: mtof(91), dur: 0.16, gain: 0.05, at: 0.05 }); },
  click() { A.tone({ type: 'triangle', freq: 1800, to: 1200, dur: 0.03, gain: 0.06 }); },
  open(kind: string) {
    if (kind === 'chest') { A.tone({ type: 'sawtooth', freq: 140, to: 220, dur: 0.35, gain: 0.05, filter: { type: 'bandpass', freq: 900, q: 6 } }); A.noiseHit({ gain: 0.15, filter: 'lowpass', freq: 600, dur: 0.12, at: 0.3 }); }
    else if (kind === 'merchant') { sfx.coins(); }
    else if (kind === 'furnace') A.noiseHit({ color: 'brown', gain: 0.35, filter: 'lowpass', freq: 400, dur: 0.6, attack: 0.15 });
    else A.tone({ type: 'triangle', freq: 660, dur: 0.1, gain: 0.06 });
  },
  deposit() { A.noiseHit({ gain: 0.25, filter: 'lowpass', freq: 500, dur: 0.15 }); A.tone({ freq: 120, to: 80, dur: 0.12, gain: 0.25 }); },
  error() { A.tone({ type: 'square', freq: 196, dur: 0.12, gain: 0.04, filter: { type: 'lowpass', freq: 900 } }); A.tone({ type: 'square', freq: 165, dur: 0.16, gain: 0.04, at: 0.09, filter: { type: 'lowpass', freq: 900 } }); },
  /** A private message arrived: a soft two-note chime. */
  pm() { [79, 86].forEach((m, i) => A.bell(mtof(m), [[1, 0.07], [2.76, 0.02]], 0.6, { at: i * 0.09, reverb: 0.3 })); },
  chat() { A.tone({ freq: 700, to: 1050, dur: 0.08, gain: 0.05 }); },
  eat() { for (let i = 0; i < 3; i++) A.noiseHit({ gain: 0.2, filter: 'bandpass', freq: rand(900, 1600), q: 2, dur: 0.07, at: i * 0.16 }); },
  teleport() { [67, 71, 74, 79, 83, 86].forEach((m, i) => A.tone({ freq: mtof(m), dur: 0.6, gain: 0.07, at: i * 0.05, reverb: 0.6 })); A.noiseHit({ gain: 0.08, filter: 'highpass', freq: 4000, to: 9000, dur: 1, attack: 0.3, reverb: 0.5 }); },
};
